import { CommentType } from '@mesa/core/drizzle';
import { ReactionEmoji, ReactionRecord, ReactionSummary } from '@mesa/core/reactions';
import { useQuery as useZeroQuery } from '@rocicorp/zero/react';
import { useParams } from '@tanstack/react-router';
import { useCallback, useMemo } from 'react';
import { ulid } from 'ulid';

import { useZero } from '@/lib/zero';
import { useUser } from '@/services/user';

interface UseReactionsProps {
  subjectId: number;
  commentType: CommentType;
}

interface UseReactionsReturn {
  reactions: ReactionRecord[];
  isLoading: boolean;
  addIssueCommentReaction: (emoji: ReactionEmoji) => Promise<void>;
  addReviewCommentReaction: (emoji: ReactionEmoji) => Promise<void>;
  addReviewBodyReaction: (emoji: ReactionEmoji) => Promise<void>;
  removeIssueCommentReaction: (gid: string) => Promise<void>;
  removeReviewCommentReaction: (gid: string) => Promise<void>;
  removeReviewBodyReaction: (gid: string) => Promise<void>;
}

export function useReactions({ subjectId, commentType }: UseReactionsProps): UseReactionsReturn {
  const { owner, repo, number: pullNumber } = useParams({ from: '/_authed/_layout/$owner/$repo/pull/$number' });

  const zero = useZero();
  const { user } = useUser();
  const currentUserId = user?.id;

  // Query reactions for this comment
  const [reactions] = useZeroQuery(
    zero.query.ReactionsTable.where(({ cmp, and }) => and(cmp('commentId', subjectId), cmp('commentType', commentType)))
  );

  const isLoading = reactions === undefined;

  const addIssueCommentReaction = useCallback(
    async (emoji: ReactionEmoji) => {
      zero.mutate.ReactionsTable.addIssueCommentReaction({
        gid: ulid(),
        commentId: subjectId,
        emoji,
        userId: currentUserId,
        owner,
        repo,
      });
    },
    [zero, subjectId, currentUserId, owner, repo]
  );

  const addReviewCommentReaction = useCallback(
    async (emoji: ReactionEmoji) => {
      zero.mutate.ReactionsTable.addReviewCommentReaction({
        gid: ulid(),
        commentId: subjectId,
        emoji,
        userId: currentUserId,
        owner,
        repo,
      });
    },
    [zero, subjectId, currentUserId, owner, repo]
  );

  const addReviewBodyReaction = useCallback(
    async (emoji: ReactionEmoji) => {
      zero.mutate.ReactionsTable.addReviewBodyReaction({
        gid: ulid(),
        reviewId: subjectId,
        pullNumber: Number(pullNumber),
        emoji,
        userId: currentUserId,
        owner,
        repo,
      });
    },
    [zero, subjectId, currentUserId, pullNumber, owner, repo]
  );

  const removeIssueCommentReaction = useCallback(
    async (gid: string) => {
      zero.mutate.ReactionsTable.removeIssueCommentReaction({
        gid,
        owner,
        repo,
      });
    },
    [zero, owner, repo]
  );

  const removeReviewCommentReaction = useCallback(
    async (gid: string) => {
      zero.mutate.ReactionsTable.removeReviewCommentReaction({
        gid,
        owner,
        repo,
      });
    },
    [zero, owner, repo]
  );

  const removeReviewBodyReaction = useCallback(
    async (gid: string) => {
      zero.mutate.ReactionsTable.removeReviewBodyReaction({
        gid,
        pullNumber: Number(pullNumber),
        owner,
        repo,
      });
    },
    [zero, pullNumber, owner, repo]
  );

  return {
    reactions,
    isLoading,
    addIssueCommentReaction,
    addReviewCommentReaction,
    addReviewBodyReaction,
    removeIssueCommentReaction,
    removeReviewCommentReaction,
    removeReviewBodyReaction,
  };
}

// Utility function to group reactions by emoji (can be used elsewhere)
export function groupReactionsByEmoji(reactions: ReactionRecord[], currentUserId: number): ReactionSummary[] {
  const grouped: Record<ReactionEmoji, ReactionSummary> = reactions.reduce(
    (acc, reaction) => {
      const emoji = reaction.emoji;
      if (!acc[emoji]) {
        acc[emoji] = {
          emoji,
          count: 0,
          userReacted: false,
        };
      }

      acc[emoji].count++;

      if (reaction.userId === currentUserId) {
        acc[emoji].userReacted = true;
      }

      return acc;
    },
    {} as Record<ReactionEmoji, ReactionSummary>
  );

  return Object.values(grouped).sort((a, b) => b.count - a.count);
}
